import { Trans, useTranslation } from 'react-i18next';

import { ReactComponent as RightArrowIcon } from '../../../../assets/icons/arrow-right.svg';
import { useBroker } from '../../../hooks/useBroker/useBroker';
import type { IProspect } from '../../../interfaces/prospects';
import type { IQuote } from '../../../interfaces/quotes';
import { TRANSLATIONS_PROSPECTS } from '../../../translations/prospects';
import {
  buildProspectActivationGroup,
  buildQuoteActivationGroup,
  getActivationQuotes
} from '../../../utils/checks/activationChecks';
import CustomButton from '../../Buttons/CustomButton/CustomButton';
import { CustomModal } from '../CustomModal/CustomModal';
import './ActivationMissingInfoModal.scss';

interface IActivationMissingInfoModalProps {
  open: boolean;
  prospect: IProspect;
  quotes?: IQuote[];
  onSaveAsPending: () => void;
  onCompleteNow: () => void;
  onClose: () => void;
}

const ActivationMissingInfoModal = ({
  open,
  prospect,
  quotes = [],
  onSaveAsPending,
  onCompleteNow,
  onClose
}: IActivationMissingInfoModalProps) => {
  const { t } = useTranslation();
  const { addonTypeById } = useBroker();
  const { title, subtitle, clientInfo, saveAsPending, completeNow } =
    TRANSLATIONS_PROSPECTS.detail.quoted.activationMissingInfo;

  const prospectGroup = buildProspectActivationGroup(prospect);
  const missingClientFields = prospectGroup.checks.filter((c) => !c.done);

  const quoteSections = getActivationQuotes(quotes)
    .map((quote) => buildQuoteActivationGroup(quote, addonTypeById))
    .map((group) => ({ id: group.id, title: group.title ?? '', missing: group.checks.filter((c) => !c.done) }))
    .filter((section) => section.missing.length > 0);

  return (
    <CustomModal open={open} title={t(title)} onClose={onClose} size="m">
      <div className="activation-missing-info-modal__body">
        <p className="activation-missing-info-modal__subtitle">
          <Trans i18nKey={subtitle} values={{ companyName: prospect.companyName }} components={{ b: <b /> }} />
        </p>

        {missingClientFields.length > 0 && (
          <div className="activation-missing-info-modal__section">
            <span className="activation-missing-info-modal__section-title">{t(clientInfo.sectionTitle)}</span>
            <ul className="activation-missing-info-modal__list">
              {missingClientFields.map((field) => (
                <li key={field.id} className="activation-missing-info-modal__item">
                  {t(field.labelKey)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {quoteSections.map((section) => (
          <div key={section.id} className="activation-missing-info-modal__section">
            <span className="activation-missing-info-modal__section-title">{section.title}</span>
            <ul className="activation-missing-info-modal__list">
              {section.missing.map((check) => (
                <li key={check.id} className="activation-missing-info-modal__item">
                  {t(check.labelKey)}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="activation-missing-info-modal__footer">
        <CustomButton variant="default" size="m" onClick={onSaveAsPending}>
          {t(saveAsPending)}
        </CustomButton>
        <CustomButton variant="primary" size="m" onClick={onCompleteNow}>
          {t(completeNow)}
          <RightArrowIcon className="activation-missing-info-modal__arrow" />
        </CustomButton>
      </div>
    </CustomModal>
  );
};

export default ActivationMissingInfoModal;
